import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';

// Modal popover for creating a custom seasonality profile.
//
// View is calendar-fixed (Jan → Dec) regardless of the scenario's
// start_month — a profile is a calendar property, not a scenario property.
// The user types one % per month ; the sum is shown live and should be
// close to 100. The profile is stored as 12 factors (applyProfile will
// renormalize anyway, so the absolute scale doesn't matter for the math).
export default function ProfileEditor({ initialProfile, onSave, onCancel }) {
  const { t } = useTranslation();
  const p = useP();
  const isEdit = !!initialProfile;
  const [name, setName] = useState(initialProfile?.label || '');
  const [pcts, setPcts] = useState(() => {
    if (!initialProfile) return Array(12).fill(0);
    const f = initialProfile.factors || [];
    const sumF = f.reduce((s, v) => s + (Number(v) || 0), 0);
    // Normalize factors to sum 100 for display convenience.
    if (sumF <= 0) return Array(12).fill(0);
    return f.map((v) => Number(((Number(v) || 0) * 100 / sumF).toFixed(2)));
  });
  const firstInputRef = useRef(null);

  useEffect(() => {
    // Autofocus the name field on mount.
    const tmr = setTimeout(() => firstInputRef.current?.focus(), 50);
    return () => clearTimeout(tmr);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const sum = pcts.reduce((s, v) => s + (Number(v) || 0), 0);
  const sumClose = Math.abs(sum - 100) < 0.5;
  const canSave = name.trim().length > 0 && sum > 0;

  function updatePct(idx, raw) {
    const v = Number(raw);
    setPcts((prev) => prev.map((pct, i) => (i === idx ? (Number.isFinite(v) ? Math.max(0, v) : 0) : pct)));
  }

  function fillConstant() {
    setPcts(Array(12).fill(Number((100 / 12).toFixed(2))));
  }

  function handleSave() {
    if (!canSave) return;
    onSave({
      id: initialProfile?.id || `custom_${Date.now()}`,
      label: name.trim(),
      factors: pcts.slice(),
    });
  }

  return (
    <div className="profile-editor-overlay" role="dialog" aria-modal="true">
      <div className="profile-editor">
        <header className="profile-editor__header">
          <h3>{isEdit ? p('Modifier le profil') : p('Nouveau profil de saisonnalité')}</h3>
          <button
            type="button"
            className="profile-editor__close"
            onClick={onCancel}
            aria-label={t('common:actions.close')}
          >×</button>
        </header>

        <p className="profile-editor__hint">
          {p('Saisir le pourcentage attribué à chaque mois calendaire (Janvier → Décembre). La somme doit être proche de 100 %. La vue est fixe pour éviter les confusions avec le mois de début du scénario.')}
        </p>

        <label className="profile-editor__name-field">
          <span>{p('Nom du profil')}</span>
          <input
            ref={firstInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={p("ex. Carnet d'heures Q2 + Q3")}
            maxLength={60}
          />
        </label>

        <div className="profile-editor__grid">
          {Array.from({ length: 12 }, (_, i) => (
            <label key={i} className="profile-editor__month">
              <span>{t(`common:months.${i + 1}`)}</span>
              <div className="profile-editor__input-wrap">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={pcts[i] === 0 ? '' : pcts[i]}
                  onChange={(e) => updatePct(i, e.target.value)}
                  placeholder="0"
                />
                <span className="profile-editor__unit">%</span>
              </div>
            </label>
          ))}
        </div>

        <div className="profile-editor__footer">
          <button type="button" className="profile-editor__btn-secondary" onClick={fillConstant}>
            {p('Répartition constante (8,33 %)')}
          </button>
          <span className={`profile-editor__sum ${sumClose ? 'is-ok' : 'is-warn'}`}>
            {p('Somme :')} {sum.toFixed(1)} %
            {!sumClose && <span className="profile-editor__sum-note">{p(' (cible 100 %)')}</span>}
          </span>
          <div className="profile-editor__actions">
            <button type="button" className="profile-editor__btn-cancel" onClick={onCancel}>
              {t('common:actions.cancel')}
            </button>
            <button
              type="button"
              className="profile-editor__btn-save"
              onClick={handleSave}
              disabled={!canSave}
            >
              {isEdit ? p('Mettre à jour') : p('Enregistrer le profil')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
